import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from './ui/button';
import {
  CheckCircle2,
  CircleDashed,
  CreditCard,
  ExternalLink,
  Loader2,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { devError } from '../utils/logger';

const StatusPill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      ok
        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
        : 'bg-secondary/40 text-muted-foreground border border-border/30'
    }`}
    data-testid={`payout-pill-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <CircleDashed className="w-3.5 h-3.5" />}
    {label}
  </span>
);

export default function PayoutsPanel() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/connect/status`);
      setStatus(data);
    } catch (e) {
      devError('connect/status failed', e);
      toast.error('Could not load payout status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // If returning from Stripe onboarding (?connect=return) refresh once more after a beat.
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect')) {
      setTimeout(refresh, 1500);
    }
  }, []);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/connect/onboard`);
      window.location.href = data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start Stripe onboarding';
      toast.error(msg);
      setBusy(false);
    }
  };

  const handleDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/connect/dashboard`);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not open Stripe dashboard';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="payouts-loading">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
      </div>
    );
  }

  const onboarded = !!status?.charges_enabled;
  const partial = !!status?.has_account && !onboarded;
  const notStarted = !status?.has_account;

  let heroTitle = 'Get paid for your sales';
  if (onboarded) heroTitle = 'Payouts active';
  else if (partial) heroTitle = 'Finish setup';

  let heroBody =
    'Connect a Stripe account so buyers pay you directly. PatinaMart takes a 1% commission on completed sales — the lowest in the marketplace world.';
  if (onboarded) {
    heroBody = 'Funds from your listing sales land in your bank account automatically.';
  } else if (partial) {
    heroBody = 'You started onboarding but Stripe still needs a few details before you can receive payouts.';
  }

  return (
    <div className="space-y-6" data-testid="payouts-panel">
      {/* Header / hero card */}
      <div className="rounded-xl border border-border/30 bg-card/40 p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex gap-4">
            <div className="w-12 h-12 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-accent mb-1">Stripe Connect</p>
              <h2 className="font-playfair text-2xl text-foreground">{heroTitle}</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">{heroBody}</p>
            </div>
          </div>

          <div className="flex flex-col gap-2 md:items-end">
            {notStarted && (
              <Button
                onClick={handleConnect}
                disabled={busy}
                className="btn-gold"
                data-testid="connect-stripe-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Connect Stripe
              </Button>
            )}
            {partial && (
              <Button
                onClick={handleConnect}
                disabled={busy}
                className="btn-gold"
                data-testid="resume-onboarding-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Continue setup
              </Button>
            )}
            {onboarded && (
              <Button
                onClick={handleDashboard}
                disabled={busy}
                variant="outline"
                className="border-border/40"
                data-testid="manage-payouts-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Manage payouts
              </Button>
            )}
            <button
              onClick={refresh}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="refresh-payout-status-btn"
            >
              Refresh status
            </button>
          </div>
        </div>
      </div>

      {/* Status pills */}
      {!notStarted && (
        <div className="flex flex-wrap gap-2" data-testid="payouts-status-pills">
          <StatusPill ok={!!status?.details_submitted} label="Details submitted" />
          <StatusPill ok={!!status?.charges_enabled} label="Charges enabled" />
          <StatusPill ok={!!status?.payouts_enabled} label="Payouts enabled" />
        </div>
      )}

      {/* What happens next / explainer */}
      <div className="rounded-xl border border-border/20 bg-card/30 p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-accent mb-3">How it works</p>
        <ol className="space-y-3 text-sm text-muted-foreground">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs flex items-center justify-center">1</span>
            <span>Click <strong className="text-foreground">Connect Stripe</strong> and complete the short onboarding form. Stripe verifies your identity and bank details.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs flex items-center justify-center">2</span>
            <span>
              When a buyer purchases your listing, funds settle to your Stripe balance. <strong className="text-foreground">PatinaMart's 1% commission</strong> is retained automatically — no per-quote, per-click or listing fees ever.{' '}
              <span className="text-muted-foreground/80">Stripe's standard payment-processing fee (≈ 1.7% + A$0.30 in AU) is deducted from your balance directly by Stripe — PatinaMart doesn't add a markup on top.</span>
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs flex items-center justify-center">3</span>
            <span>Stripe pays out to your bank account on a rolling schedule. You can review every transaction inside the Stripe dashboard.</span>
          </li>
        </ol>
      </div>

      {/* Failure / partial state hint */}
      {partial && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-300/90 flex gap-3" data-testid="onboarding-incomplete-banner">
          <XCircle className="w-5 h-5 flex-shrink-0 text-amber-400 mt-0.5" />
          <div>
            <p className="font-medium text-amber-200">Onboarding not yet complete</p>
            <p className="text-xs mt-1 text-amber-200/70">
              Stripe still needs more information. Click <em>Continue setup</em> above to finish — you can always come back later.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
